import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Upload, X, Home, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Popup {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  button_text: string;
  button_url?: string;
  featured_image?: string;
  display_on: 'home' | 'blog' | 'both';
  created_at: string;
}

const PopupManager = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [editingPopup, setEditingPopup] = useState<Popup | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    is_active: false,
    button_text: "Tutup",
    button_url: "",
    featured_image: "",
    display_on: 'home' as 'home' | 'blog' | 'both'
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchPopups();
  }, []);

  const fetchPopups = async () => {
    try {
      const { data, error } = await supabase
        .from('popups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Ensure all required fields are present and provide defaults if needed
      const popupsWithDefaults = (data || []).map((popup: any): Popup => ({
        id: popup.id || '',
        title: popup.title || '',
        content: popup.content || '',
        is_active: popup.is_active || false,
        button_text: popup.button_text || 'Tutup',
        button_url: popup.button_url || '',
        featured_image: popup.featured_image || '',
        display_on: popup.display_on || 'home',
        created_at: popup.created_at || new Date().toISOString()
      }));
      
      setPopups(popupsWithDefaults);
    } catch (error) {
      console.error('Error fetching popups:', error);
      toast({
        title: "Error",
        description: "Gagal memuat popup",
        variant: "destructive",
      });
    }
  };

  const handleCreate = async () => {
    try {
      const { error } = await supabase
        .from('popups')
        .insert([formData]);

      if (error) throw error;
      
      toast({
        title: "Berhasil",
        description: "Popup berhasil dibuat",
      });
      
      resetForm();
      fetchPopups();
    } catch (error) {
      console.error('Error creating popup:', error);
      toast({
        title: "Error",
        description: "Gagal membuat popup",
        variant: "destructive",
      });
    }
  };

  const handleUpdate = async () => {
    if (!editingPopup) return;
    
    try {
      const { error } = await supabase
        .from('popups')
        .update(formData)
        .eq('id', editingPopup.id);

      if (error) throw error;
      
      toast({
        title: "Berhasil",
        description: "Popup berhasil diperbarui",
      });
      
      resetForm();
      fetchPopups();
    } catch (error) {
      console.error('Error updating popup:', error);
      toast({
        title: "Error",
        description: "Gagal memperbarui popup",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('popups')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Berhasil",
        description: "Popup berhasil dihapus",
      });
      
      fetchPopups();
    } catch (error) {
      console.error('Error deleting popup:', error);
      toast({
        title: "Error",
        description: "Gagal menghapus popup",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      is_active: false,
      button_text: "Tutup",
      button_url: "",
      featured_image: "",
      display_on: 'home'
    });
    setImagePreview(null);
    setEditingPopup(null);
    setIsCreating(false);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validasi ukuran file (maksimal 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Ukuran file terlalu besar",
        description: "Ukuran file maksimal 2MB",
        variant: "destructive",
      });
      return;
    }

    // Validasi tipe file
    if (!file.type.match('image.*')) {
      toast({
        title: "Format file tidak didukung",
        description: "Hanya file gambar yang diperbolehkan",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      const fileName = `popup-${Date.now()}-${file.name}`;
      const filePath = `popup-images/${fileName}`;

      // Upload ke Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Dapatkan URL publik
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      setImagePreview(publicUrl);
      setFormData(prev => ({
        ...prev,
        featured_image: publicUrl
      }));

      toast({
        title: "Gambar berhasil diunggah",
        description: "Gambar popup berhasil disimpan",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Gagal mengunggah gambar",
        description: "Terjadi kesalahan saat mengunggah gambar",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      featured_image: ""
    }));
  };

  const handleEdit = (popup: Popup) => {
    setEditingPopup(popup);
    setIsCreating(true);
    setFormData({
      title: popup.title,
      content: popup.content,
      is_active: popup.is_active,
      button_text: popup.button_text,
      button_url: popup.button_url || "",
      featured_image: popup.featured_image || "",
      display_on: popup.display_on || 'home'
    });
    
    if (popup.featured_image) {
      setImagePreview(popup.featured_image);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>{isCreating ? (editingPopup ? 'Edit Popup' : 'Buat Popup Baru') : 'Manajemen Popup'}</CardTitle>
              <CardDescription>Kelola popup selamat datang untuk pengunjung website</CardDescription>
            </div>
            {!isCreating && (
              <Button onClick={() => setIsCreating(true)} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Tambah Popup
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isCreating ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="popup-title">Judul Popup *</Label>
                <Input
                  id="popup-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Masukkan judul popup"
                />
              </div>

              <div className="space-y-2">
                <Label>Tampilkan di</Label>
                <Select
                  value={formData.display_on}
                  onValueChange={(value) => setFormData({...formData, display_on: value as 'home' | 'blog' | 'both'})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pilih halaman" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        <span>Halaman Utama Saja</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="blog">
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        <span>Halaman Blog Saja</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="both">
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        <BookOpen className="h-4 w-4" />
                        <span>Kedua Halaman</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="popup-content">Konten Popup *</Label>
                <Textarea
                  id="popup-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Masukkan konten popup"
                  rows={4}
                />
              </div>
              
              <div>
                <Label htmlFor="popup-button-text">Teks Tombol</Label>
                <Input
                  id="popup-button-text"
                  value={formData.button_text}
                  onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                  placeholder="Tutup"
                />
              </div>
              
              <div>
                <Label htmlFor="popup-button-url">URL Tombol (opsional)</Label>
                <Input
                  id="popup-button-url"
                  value={formData.button_url}
                  onChange={(e) => setFormData({ ...formData, button_url: e.target.value })}
                  placeholder="https://example.com atau #basic"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Gunakan #anchor untuk scroll ke section, atau URL lengkap untuk link eksternal
                </p>
              </div>

              <div className="space-y-2">
                <Label>Gambar Popup (opsional)</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <label
                      htmlFor="popup-image-upload"
                      className={`flex flex-col items-center justify-center w-32 h-20 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isUploading ? (
                        <div className="text-sm text-gray-500">Mengunggah...</div>
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-gray-400 mb-1" />
                          <div className="text-sm text-gray-500 text-center px-2">
                            {imagePreview ? 'Ganti Gambar' : 'Unggah Gambar'}
                          </div>
                        </>
                      )}
                      <input
                        id="popup-image-upload"
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                  
                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-32 h-20 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        aria-label="Hapus gambar"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Rekomendasi: Rasio 16:9, maks. 2MB
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="popup-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="popup-active">Aktifkan popup</Label>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={editingPopup ? handleUpdate : handleCreate}
                  className="flex-1"
                  disabled={!formData.title || !formData.content}
                >
                  {editingPopup ? 'Perbarui Popup' : 'Buat Popup'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={resetForm}
                  className="flex-1"
                >
                  Batal
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {popups.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">Belum ada popup</h3>
                  <p className="text-gray-500 mb-4">Buat popup pertama untuk menyambut pengunjung website</p>
                  <Button onClick={() => setIsCreating(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Buat Popup
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {popups.map((popup) => (
                    <Card key={popup.id} className="p-4 bg-white/50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold">{popup.title}</h3>
                            <Badge variant={popup.is_active ? "default" : "secondary"}>
                              {popup.is_active ? 'Aktif' : 'Nonaktif'}
                            </Badge>
                            <Badge variant="outline">
                              {popup.display_on === 'home' && 'Home'}
                              {popup.display_on === 'blog' && 'Blog'}
                              {popup.display_on === 'both' && 'Home & Blog'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{popup.content}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Tombol: {popup.button_text}</span>
                            {popup.button_url && <span>URL: {popup.button_url}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(popup)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(popup.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PopupManager;
